import React, { useEffect, useState, useMemo } from 'react'
import { ScoopButton, ScoopSelector, ScoopSpinner, ScoopCenter } from '../../../../ui'
import { useWorkspaceMetadata } from '../../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { useSelector } from 'react-redux'
import { Server } from '../../../../../api/Server'
import { ScoopTab } from '../../../../ui'
import { TargetColumn } from '../TargetColumn/TargetColumn'
import { Cluster } from '../Cluster/Cluster'
import { PickColumns } from '../PickColumns/PickColumns'
import { FilterAndTimeRange } from '../FilterAndTimeRange/FilterAndTimeRange'
import { GroupAnalysis } from '../GroupAnalysis/GroupAnalysis'
import { EVENTS, trackEvent } from '../../../../../events'
import { TimeAnalysis } from '../TimeAnalysis/TimeAnalysis'
import styles from './ML.module.scss'

export const ML = ({
    selectedInbox,
    setSelectedInbox,
    selectedTab,
    setSelectedTab,
    mlResults,
    setMLResults,
    mlConfig,
    setMlConfig,
    fieldList,
    filterList,
}) => {
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const token = useSelector((state) => state.auth.token)
    const { workspaceMetadata } = useWorkspaceMetadata()
    const [server, setServer] = useState(new Server(workspaceID, userID, token))
    const [pickColumnsDialogOpen, setPickColumnsDialogOpen] = React.useState(false)
    const [pickFiltersDialogOpen, setPickFiltersDialogOpen] = React.useState(false)
    const [dialogSelectedFields, setDialogSelectedFields] = useState([])
    const [dialogSelectedFilters, setDialogSelectedFilters] = useState([])
    const [working, setWorking] = useState(false)
    const [intervalId, setIntervalId] = useState('')
    const [currentStatus, setCurrentStatus] = useState('Working')

    useEffect(() => {
        if (working) {
            const newIntervalId = setInterval(checkStatus, 5000)
            setIntervalId(newIntervalId)
        } else {
            clearInterval(intervalId)
        }
    }, [working])

    const checkStatus = () => {
        if (token) {
            const server = new Server(workspaceID, userID, token)
            server.postData(
                {
                    action: 'getObjects',
                    class: 'scoop.process.AIStatus',
                    attributeName: 'inboxID',
                    attributeValue: selectedInbox.inboxID,
                    workspaceID: workspaceID,
                },
                processStatusResult
            )
        }
    }

    const processStatusResult = (result) => {
        if (result && result.objects.length > 0) {
            if (result.objects[0].type === 'result') {
                let mlRes = JSON.parse(result.objects[0].currentStatus)
                server.postData(
                    {
                        action: 'deleteObject',
                        class: 'scoop.process.AIStatus',
                        key: selectedInbox.inboxID,
                    },
                    () => {
                        setMLResults(mlRes)
                        setWorking(false)
                    }
                )
            } else {
                setCurrentStatus(result.objects[0].currentStatus)
                setWorking(true)
            }
        } else {
            setCurrentStatus(null)
            setWorking(false)
        }
    }

    function setInbox(inboxID) {
        let newInbox = workspaceMetadata?.inboxes.find((x) => x.inboxID === inboxID)
        setSelectedInbox(newInbox)
    }

    async function analyze(jsonBody) {
        if (jsonBody) {
            setMLResults(null)
            setCurrentStatus('Starting....')
            setWorking(true)
            setJsonBody(jsonBody)
            trackEvent(EVENTS.DATA_SCIENCE.PROCESS_ANALYSIS, {
                ai: {
                    action: jsonBody.action,
                },
            })
            await server.sheetPostData(jsonBody, (data) => {
                checkStatus()
            })
        }
    }

    function setJsonBody(jsonBody) {
        jsonBody.inboxID = selectedInbox.inboxID
        jsonBody.selectedFields = mlConfig.selectedFields
        jsonBody.selectedFilters = mlConfig.selectedFilters
        if (mlConfig.filter) {
            jsonBody.filter = mlConfig.filter
        }
        jsonBody.filterDate = mlConfig.filterDate
        jsonBody.timeRange = mlConfig.config.timeRange
        if (mlConfig.config.fromPeriod) {
            jsonBody.fromPeriod = mlConfig.config.fromPeriod
        }
        if (mlConfig.config.toPeriod) {
            jsonBody.toPeriod = mlConfig.config.toPeriod
        }
    }

    let dateAnalysisValid =
        selectedInbox &&
        selectedInbox.tables &&
        selectedInbox.tables.length > 0 &&
        selectedInbox.tables[0].dates &&
        (selectedInbox.transactional ||
            (selectedInbox.tables[0].dates &&
                (selectedInbox.tables[0].dates.length > 1 ||
                    selectedInbox.tables[0].dates[0] !== 'timestamp')))

    return (
        <>
            {working && (
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingStatus}>Analyzing: {currentStatus}</div>
                    <div>
                        <ScoopSpinner size={42} />
                    </div>
                </div>
            )}
            {!working && (
                <div className={styles.mainContainer}>
                    {!mlResults && (
                        <div className={styles.datasetSelector}>
                            <ScoopSelector
                                label="Select a dataset to work with"
                                value={selectedInbox.inboxID}
                                onChange={(e) => setInbox(e.target.value)}
                                options={
                                    workspaceMetadata?.inboxes
                                        .filter((i) => i.tables.length)
                                        .map((inbox) => ({
                                            label: inbox.label,
                                            value: inbox.inboxID,
                                        })) || []
                                }
                                size="sm"
                                labelClassName="selector-label-bold"
                                removeMargin
                            />
                        </div>
                    )}
                    {selectedInbox && !mlResults && (
                        <div className={styles.configButtonsRow}>
                            <ScoopButton
                                className={styles.configButton}
                                variant="grey"
                                size="sm"
                                onClick={() => {
                                    setPickColumnsDialogOpen(true)
                                    setDialogSelectedFields(
                                        mlConfig.selectedFields ? mlConfig.selectedFields : []
                                    )
                                }}
                            >
                                <div className={styles.configButtonContent}>
                                    <div>Dataset Columns to Use in Analysis</div>
                                    {mlConfig.selectedFields &&
                                        mlConfig.selectedFields.length > 0 && (
                                            <p className={styles.configDetails}>
                                                <strong>Selected columns:</strong>&nbsp;
                                                {mlConfig.selectedFields &&
                                                    mlConfig.selectedFields.length > 0 &&
                                                    mlConfig.selectedFields.length > 20 && (
                                                        <>
                                                            {mlConfig.selectedFields.length} columns
                                                        </>
                                                    )}
                                                {mlConfig.selectedFields &&
                                                    mlConfig.selectedFields.length > 0 &&
                                                    mlConfig.selectedFields.length < 20 && (
                                                        <>
                                                            {mlConfig.selectedFields
                                                                .map((field) => field)
                                                                .join(', ')}
                                                        </>
                                                    )}
                                            </p>
                                        )}
                                </div>
                            </ScoopButton>
                            <ScoopButton
                                className={styles.configButton}
                                variant="grey"
                                size="sm"
                                onClick={() => {
                                    setDialogSelectedFilters(
                                        mlConfig.selectedFilters ? mlConfig.selectedFilters : []
                                    )
                                    setPickFiltersDialogOpen(true)
                                }}
                            >
                                <div className={styles.configButtonContent}>
                                    <div>Set Filters and Control Time Range</div>
                                    {mlConfig && mlConfig.config && (
                                        <p className={styles.configDetails}>
                                            <strong>Time range:</strong>&nbsp;
                                            {mlConfig.config.timeRange}
                                        </p>
                                    )}
                                    {mlConfig && mlConfig.filter && (
                                        <p className={styles.configDetails}>
                                            <strong>Chat filter:</strong>&nbsp;
                                            {mlConfig.filterDescription}
                                        </p>
                                    )}{' '}
                                    {mlConfig.selectedFilters &&
                                        mlConfig.selectedFilters.length > 0 && (
                                            <p className={styles.configDetails}>
                                                <strong>Selected saved filters:</strong>
                                                &nbsp;
                                                {filterList
                                                    .filter((filter) =>
                                                        mlConfig.selectedFilters.includes(filter.id)
                                                    )
                                                    .map((filter) => filter.value)
                                                    .join(', ')}
                                            </p>
                                        )}
                                </div>
                            </ScoopButton>
                        </div>
                    )}
                    {mlResults && mlResults.error && (
                        <div className={styles.errorContainer}>
                            <p className={styles.errorText}>Error: {mlResults.error.message}</p>
                        </div>
                    )}
                    {selectedInbox && (
                        <div className={styles.analysisTypeSelector}>
                            <ScoopTab
                                tabs={[
                                    { key: 'cluster', label: 'Find Segments/clusters' },
                                    { key: 'column', label: 'Identify predictors of a column' },
                                    { key: 'group', label: 'Explain/analyze a group' },
                                    ...(dateAnalysisValid
                                        ? [{ key: 'time', label: 'Compare two Time Periods' }]
                                        : []),
                                ]}
                                value={mlConfig.modelType}
                                onChange={(_, value) => {
                                    mlConfig.modelType = value
                                    setMlConfig({ ...mlConfig })
                                    setMLResults(null)
                                }}
                            />
                        </div>
                    )}
                    {selectedInbox && (
                        <>
                            {mlConfig.modelType === 'column' && (
                                <TargetColumn
                                    mlResults={mlResults}
                                    selectedInbox={selectedInbox}
                                    analyze={analyze}
                                    mlConfig={mlConfig}
                                    setMlConfig={setMlConfig}
                                />
                            )}
                            {mlConfig.modelType === 'cluster' && (
                                <Cluster
                                    mlResults={mlResults}
                                    analyze={analyze}
                                    server={server}
                                    selectedInbox={selectedInbox}
                                    mlConfig={mlConfig}
                                    setMlConfig={setMlConfig}
                                />
                            )}
                            {mlConfig.modelType === 'group' && (
                                <GroupAnalysis
                                    mlResults={mlResults}
                                    server={server}
                                    filterList={filterList}
                                    selectedInbox={selectedInbox}
                                    analyze={analyze}
                                    mlConfig={mlConfig}
                                    setMlConfig={setMlConfig}
                                />
                            )}
                            {mlConfig.modelType === 'time' && dateAnalysisValid && (
                                <TimeAnalysis
                                    mlResults={mlResults}
                                    selectedInbox={selectedInbox}
                                    analyze={analyze}
                                    setSelectedTab={setSelectedTab}
                                    mlConfig={mlConfig}
                                    setMlConfig={setMlConfig}
                                />
                            )}
                            {mlResults && (
                                <ScoopCenter className={styles.doneButtonContainer}>
                                    <ScoopButton
                                        onClick={() => {
                                            setMLResults(null)
                                        }}
                                        variant="default"
                                    >
                                        Done
                                    </ScoopButton>
                                </ScoopCenter>
                            )}
                        </>
                    )}
                    <PickColumns
                        pickColumnsDialogOpen={pickColumnsDialogOpen}
                        setPickColumnsDialogOpen={setPickColumnsDialogOpen}
                        setSelectedFields={(newSelectedFields) => {
                            mlConfig.selectedFields = newSelectedFields
                            setMlConfig({ ...mlConfig })
                        }}
                        setDialogSelectedFields={setDialogSelectedFields}
                        dialogSelectedFields={dialogSelectedFields}
                        targetColumn={mlConfig.targetColumn}
                        fieldList={fieldList}
                    />
                    <FilterAndTimeRange
                        server={server}
                        title={`Filter your dataset before analysis`}
                        pickFiltersDialogOpen={pickFiltersDialogOpen}
                        setPickFiltersDialogOpen={setPickFiltersDialogOpen}
                        dialogSelectedFilters={dialogSelectedFilters}
                        selectedInbox={selectedInbox}
                        setDialogSelectedFilters={setDialogSelectedFilters}
                        filterDate={mlConfig.filterDate}
                        setFilterDate={(newFilterDate) => {
                            mlConfig.filterDate = newFilterDate
                            setMlConfig({ ...mlConfig })
                        }}
                        setSelectedFilters={(newFilters) => {
                            mlConfig.selectedFilters = newFilters
                            setMlConfig({ ...mlConfig })
                        }}
                        config={mlConfig.config}
                        setConfig={(newConfig) => {
                            mlConfig.config = newConfig
                            setMlConfig({ ...mlConfig })
                        }}
                        filterList={filterList}
                        existingFilter={mlConfig.filterDescription}
                    />
                </div>
            )}
        </>
    )
}
